import { useState, useCallback } from 'react'
import { useFileUrl } from './useFileUrl'
import { addTimestampToUrl } from '../utils/utils'

export type TRefreshFiles = () => Promise<Array<{ id: number; signedUrl?: string }> | null>

interface UseFileUrlRefreshProps {
  fileId: number
  requestId: number
  initialUrl: string
  onUrlRefresh?: (newUrl: string) => void
  refreshFiles?: TRefreshFiles
}

export const useFileUrlRefresh = ({
  fileId,
  requestId,
  initialUrl,
  onUrlRefresh,
  refreshFiles,
}: UseFileUrlRefreshProps) => {
  const [currentUrl, setCurrentUrl] = useState(initialUrl)
  const [hasError, setHasError] = useState(false)
  const publisherRequest = useFileUrl({ requestId })
  const { isRefreshing, error } = publisherRequest
  const refreshRequest = refreshFiles ?? publisherRequest.refreshRequest

  const updateUrl = useCallback(
    (newUrl: string) => {
      const urlWithTimestamp = addTimestampToUrl(newUrl)
      setCurrentUrl(urlWithTimestamp)
      onUrlRefresh?.(newUrl)
    },
    [onUrlRefresh],
  )

  const handleError = useCallback(async () => {
    if (isRefreshing) {
      return
    }

    try {
      const files = await refreshRequest()
      if (files) {
        const file = files.find((f: { id: number; signedUrl?: string }) => f.id === fileId)
        const newUrl = file?.signedUrl
        if (newUrl) {
          updateUrl(newUrl)
          setHasError(false)
        } else {
          setHasError(true)
        }
      } else {
        setHasError(true)
      }
    } catch (err) {
      setHasError(true)
    }
  }, [isRefreshing, refreshRequest, fileId, updateUrl])

  const handleRetry = useCallback(async () => {
    setHasError(false)
    try {
      const files = await refreshRequest()
      if (files) {
        const file = files.find((f: { id: number; signedUrl?: string }) => f.id === fileId)
        const newUrl = file?.signedUrl
        if (newUrl) {
          updateUrl(newUrl)
          setHasError(false)
        } else {
          setHasError(true)
        }
      } else {
        setHasError(true)
      }
    } catch (err) {
      setHasError(true)
    }
  }, [refreshRequest, fileId, updateUrl])

  const resetUrl = useCallback((newUrl: string) => {
    setCurrentUrl(newUrl)
    setHasError(false)
  }, [])

  return {
    currentUrl,
    hasError,
    isRefreshing,
    error,
    handleError,
    handleRetry,
    resetUrl,
  }
}
